import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SalesUnitDetailHeader from "./SalesUnitDetailHeader";
import SalesUnitDetailInfo from "./SalesUnitDetailInfo";
import SalesUnitChildrenPanel from "./SalesUnitChildrenPanel";
import ProcessFlowEditorDrawer from "./ProcessFlowEditorDrawer";
import ApplyConfigurationModal from "./ApplyConfigurationModal";
import SalesUnitFormDrawer from "../SalesUnitFormDrawer";
import { applySalesUnitConfiguration, getActiveSalesUnits, getSalesUnit } from "redux/crm/salesUnitSlice";
import useBreadcrumb from "hooks/useBreadcrumb";

const SalesUnitDetail = () => {
  const params = useParams();
  const { recordData } = useSelector((state) => state.crm_sales_unit);
  const dispatch = useDispatch();

  // null = create, object = edit, undefined = drawer closed.
  const [drawerRecord, setDrawerRecord] = useState(undefined);
  const [showFlowEditor, setShowFlowEditor] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [applyCandidates, setApplyCandidates] = useState([]);
  // Bumped after a bulk apply so the children table re-reads changed values.
  const [childrenVersion, setChildrenVersion] = useState(0);

  // Breadcrumb: show the sales unit name instead of the id.
  useBreadcrumb(
    recordData
      ? [
          { label: 'Sales Units', to: '/crm/sales-units/list' },
          { label: recordData.name || `Sales Unit ${params.id}` }
        ]
      : null
  );

  useEffect(() => {
    dispatch(getSalesUnit(params.id));
  }, [params.id, dispatch]);

  const refresh = () => dispatch(getSalesUnit(params.id));

  const openApply = async () => {
    try {
      const units = await dispatch(getActiveSalesUnits()).unwrap();
      setApplyCandidates(Array.isArray(units) ? units : []);
      setShowApply(true);
    } catch (error) {
      toast.error(error?.message || 'Failed to load sales units.');
    }
  };

  const applyToUnits = async (targetIds, groups) => {
    try {
      const result = await dispatch(applySalesUnitConfiguration({ salesUnitId: recordData.id, targetIds, groups })).unwrap();
      toast.success(result.message);
      setChildrenVersion((version) => version + 1);
    } catch (error) {
      toast.error(error?.message || 'Failed to apply configuration.');
      throw error;
    }
  };

  // Only the current record belongs on the detail page; the store may still
  // hold the previous unit for a moment while navigating between units.
  const salesUnit = recordData && String(recordData.id) === String(params.id) ? recordData : null;

  return (
    <>
      <SalesUnitDetailHeader
        sales_unit={salesUnit}
        onNew={() => setDrawerRecord(null)}
        onEdit={() => setDrawerRecord(salesUnit)}
      />
      <SalesUnitDetailInfo
        sales_unit={salesUnit}
        onEditProcessFlow={() => setShowFlowEditor(true)}
        onApplyConfiguration={openApply}
      />
      <SalesUnitChildrenPanel salesUnit={salesUnit} refreshToken={childrenVersion} />

      <SalesUnitFormDrawer
        show={drawerRecord !== undefined}
        onHide={() => setDrawerRecord(undefined)}
        salesUnit={drawerRecord || undefined}
        onSaved={refresh}
      />

      {salesUnit && (
        <>
          <ProcessFlowEditorDrawer show={showFlowEditor} onHide={() => setShowFlowEditor(false)} salesUnit={salesUnit} />
          <ApplyConfigurationModal
            show={showApply}
            onHide={() => setShowApply(false)}
            salesUnit={salesUnit}
            candidates={applyCandidates}
            onApply={applyToUnits}
          />
        </>
      )}
    </>
  );
};

export default SalesUnitDetail;
